import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePractice } from '../contexts/PracticeContext';
import AuthContext from '../contexts/AuthContext';
import {
  Container,
  Typography,
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Tooltip,
  TablePagination
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

export default function Collections() {
  const apiBase = process.env.REACT_APP_PORTAL_API_URL || '';
  const { tokens } = useContext(AuthContext);
  const navigate = useNavigate();
  const { selectedPractice } = usePractice();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Search and filter state
  const [searchInput, setSearchInput] = useState('');
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);

  // Debounce search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setSearchText(searchInput);
      setPage(0);
    }, 300);
    return () => clearTimeout(handler);
  }, [searchInput]);

  // Fetch collection events
  useEffect(() => {
    if (!tokens?.id_token) return;
    if (!selectedPractice?.id) return;
    setLoading(true);
    setError(null);
    const params = new URLSearchParams();
    params.append('skip', page * rowsPerPage);
    params.append('limit', rowsPerPage);
    if (searchText) params.append('search_text', searchText);
    if (statusFilter !== 'all') params.append('status', statusFilter);
    const url = `${apiBase}/organizations/${selectedPractice.id}/collection-events?${params.toString()}`;
    fetch(url, {
      headers: { Authorization: `Bearer ${tokens.id_token}` }
    })
      .then(res => {
        if (!res.ok) throw new Error('Failed to fetch collection events');
        return res.json();
      })
      .then(data => {
        setEvents(data.items || []);
        setTotal(data.total || 0);
      })
      .catch(err => {
        console.error('Error fetching collection events:', err);
        setError(err.message);
      })
      .finally(() => setLoading(false));
  }, [tokens, selectedPractice, page, rowsPerPage, searchText, statusFilter]);

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  return (
    <Container disableGutters sx={{ mt: 2 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Collections
      </Typography>
      {/* Search and Status filter */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <TextField
          size="small"
          label="Search Collections"
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          sx={{ width: 300 }}
          InputProps={{
            endAdornment: searchInput ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchInput('')}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null
          }}
        />
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel id="status-filter-label">Status</InputLabel>
          <Select
            labelId="status-filter-label"
            label="Status"
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
          >
            <MenuItem value="all">All Statuses</MenuItem>
            <MenuItem value="NotStarted">Not Started</MenuItem>
            <MenuItem value="Completed">Completed</MenuItem>
          </Select>
        </FormControl>
      </Box>
      <TableContainer component={Paper}>
        {error ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <Typography color="error">{error}</Typography>
          </Box>
        ) : loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Table>
            <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Patient</TableCell>
              <TableCell>MRN</TableCell>
              <TableCell>Practice</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created At</TableCell>
              <TableCell>Updated At</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
            </TableHead>
            <TableBody>
              {events.map(e => (
                <TableRow key={e.id}>
                <TableCell>
                  <Box display="flex" alignItems="center">
                    <Tooltip title={e.id} arrow>
                      <Typography variant="body2" sx={{ cursor: 'default' }}>
                        {e.id.split('-')[0]}
                      </Typography>
                    </Tooltip>
                    <Tooltip title="Copy full ID" arrow>
                      <IconButton size="small" onClick={() => navigator.clipboard.writeText(e.id)}>
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
                <TableCell>{e.patient_first_name} {e.patient_last_name}</TableCell>
                <TableCell>{e.patient_mrn}</TableCell>
                <TableCell>{e.practice_name}</TableCell>
                <TableCell>{e.status}</TableCell>
                <TableCell>{new Date(e.created_at).toLocaleString()}</TableCell>
                <TableCell>{e.updated_at ? new Date(e.updated_at).toLocaleString() : ''}</TableCell>
                <TableCell>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => navigate(`/collections/${e.id}`)}
                  >
                    {e.status === 'Completed' ? 'View' : 'Start'}
                  </Button>
                </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={total}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Container>
  );
}